/**
 * Pulls the top stack frame out of an Error.stack string and returns it as
 * a `{ filePath, lineNumber, functionName }` triple suitable for Cloud
 * Error Reporting's `context.reportLocation`. Without a populated
 * reportLocation, errors group only by message text and lose the per-build
 * source pin that makes the dashboard useful.
 *
 * Browsers don't agree on stack format, so we try three patterns in order:
 *   - V8 named:     `    at funcName (https://app/file.js:42:10)`
 *   - V8 anonymous: `    at https://app/file.js:42:10`
 *   - Spidermonkey: `funcName@https://app/file.js:42:10`
 */

export interface ReportLocation {
  filePath: string;
  lineNumber: number;
  functionName: string;
}

const V8_NAMED = /^\s*at\s+([^\s(]+(?:\s+\[as\s+[^\]]+\])?)\s+\((.+):(\d+):(\d+)\)\s*$/;
const V8_ANON = /^\s*at\s+(.+):(\d+):(\d+)\s*$/;
const SPIDERMONKEY = /^([^@\s]+)?@(.+):(\d+):(\d+)\s*$/;

const FALLBACK: ReportLocation = {
  filePath: 'unknown',
  lineNumber: 0,
  functionName: 'unknown',
};

function toLineNumber(raw: string | undefined): number {
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function parseReportLocation(stack: string | undefined): ReportLocation {
  if (!stack) return FALLBACK;
  const lines = stack.split('\n');

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    const v8Named = line.match(V8_NAMED);
    if (v8Named) {
      return {
        filePath: v8Named[2],
        lineNumber: toLineNumber(v8Named[3]),
        functionName: v8Named[1],
      };
    }

    const spidermonkey = line.match(SPIDERMONKEY);
    if (spidermonkey) {
      return {
        filePath: spidermonkey[2],
        lineNumber: toLineNumber(spidermonkey[3]),
        functionName: spidermonkey[1] || '<anonymous>',
      };
    }

    const v8Anon = line.match(V8_ANON);
    if (v8Anon) {
      return {
        filePath: v8Anon[1],
        lineNumber: toLineNumber(v8Anon[2]),
        functionName: '<anonymous>',
      };
    }
  }

  return FALLBACK;
}
